import { EKnowledgeProcessingStatus } from "@frontend-shared/types";
import { FileText, Trash } from "lucide-react";
import React, { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { KnowledgeFailedStatusBadge } from "@/pages/knowledge/components/KnowledgeFailedStatusBadge";
import { KnowledgeTextExtractionWarningChip } from "@/pages/knowledge/components/KnowledgeTextExtractionWarningChip";

import type { IFolderKnowledgeFileRowProps } from "./types";

// NOTE: One stored file in a chat folder's knowledge panel. NSAI-7817: a file
// that failed to index shows the translated reason — NSAI-7345: in its Failed
// badge's tooltip, as the Knowledge page does.
const FolderKnowledgeFileRowInner: React.FC<IFolderKnowledgeFileRowProps> = ({
  file,
  onDelete,
}) => {
  const { t } = useTranslation("sidebar");
  const isFailed = file.processingStatus === EKnowledgeProcessingStatus.FAILED;

  const statusLabel = useMemo((): string | null => {
    if (
      file.processingStatus === EKnowledgeProcessingStatus.PENDING ||
      file.processingStatus === EKnowledgeProcessingStatus.PROCESSING
    ) {
      return t("chat_folder_list.folder_settings.file_processing");
    }
    return null;
  }, [file.processingStatus, t]);

  const handleDelete = useCallback(
    (): void => onDelete(file),
    [file, onDelete],
  );

  return (
    <li
      className="rounded-md border border-border px-3 py-2 text-sm"
      data-testid={`folder-knowledge-file-${file.uuid}`}
    >
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate">{file.name}</span>
        {statusLabel && (
          <span className="shrink-0 text-xs text-muted-foreground">
            {statusLabel}
          </span>
        )}
        {isFailed && (
          <KnowledgeFailedStatusBadge
            code={file.processingErrorCode}
            rawError={file.processingError}
            testId={`folder-knowledge-file-processing-error-${file.uuid}`}
          />
        )}
        {/* NOTE: NSAI-7345 — a chat in this folder answers from only the text
            that was read, so the folder panel warns as the Knowledge page does. */}
        {file.processingStatus === EKnowledgeProcessingStatus.COMPLETED &&
          file.textExtractionWarning && (
            <KnowledgeTextExtractionWarningChip
              warning={file.textExtractionWarning}
              testId={`folder-knowledge-file-text-extraction-warning-${file.uuid}`}
            />
          )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={handleDelete}
          data-testid={`folder-knowledge-file-delete-${file.uuid}`}
        >
          <Trash className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
};

export const FolderKnowledgeFileRow = memo(FolderKnowledgeFileRowInner);
